// VersionModal.tsx – deployment version (Backend + Frontend) in a modal
import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { getDeploymentVersion, DeploymentVersion } from '../../services/api';

interface VersionModalProps {
  open: boolean;
  onClose: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function VersionBlock({
  title,
  version,
  loading,
  error,
  fallbackLabel,
}: {
  title: string;
  version: DeploymentVersion | null;
  loading: boolean;
  error: string | null;
  fallbackLabel: string;
}) {
  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
        <p className="text-gray-500 text-sm">Loading…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }
  if (!version || version.message) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
        <p className="text-gray-500 text-sm">{version?.message || fallbackLabel}</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">
        {title}
      </h3>
      <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-2 text-sm">
        <dt className="text-gray-500 font-medium">Commit</dt>
        <dd className="font-mono text-gray-900 break-all">
          {version.commitShort ?? version.commitHash ?? '—'}
        </dd>

        <dt className="text-gray-500 font-medium">Tag</dt>
        <dd className="text-gray-900 break-words">{version.tag ?? '—'}</dd>

        <dt className="text-gray-500 font-medium">Commit date</dt>
        <dd className="text-gray-900">{formatDate(version.date)}</dd>

        {version.generatedAt ? (
          <>
            <dt className="text-gray-500 font-medium">Version file generated</dt>
            <dd className="text-gray-900">{formatDate(version.generatedAt)}</dd>
          </>
        ) : null}

        <dt className="text-gray-500 font-medium">Author</dt>
        <dd className="text-gray-900">
          {version.author ?? '—'}
          {version.authorEmail ? (
            <span className="text-gray-500 text-xs block mt-0.5">{version.authorEmail}</span>
          ) : null}
        </dd>

        <dt className="text-gray-500 font-medium">Subject</dt>
        <dd className="text-gray-900 break-words">{version.subject ?? '—'}</dd>
      </dl>
      {version.body ? (
        <div className="mt-3 pt-2 border-t border-gray-200">
          <div className="text-gray-500 font-medium text-sm mb-1">Message</div>
          <pre className="bg-white border border-gray-100 p-2 rounded text-xs whitespace-pre-wrap break-words max-h-28 overflow-y-auto text-gray-700">
            {version.body}
          </pre>
        </div>
      ) : null}
    </div>
  );
}

const getFrontendVersionUrl = () =>
  `${window.location.origin}${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/version.json?t=${Date.now()}`;

export const VersionModal: React.FC<VersionModalProps> = ({ open, onClose }) => {
  const [backendVersion, setBackendVersion] = useState<DeploymentVersion | null>(null);
  const [frontendVersion, setFrontendVersion] = useState<DeploymentVersion | null>(null);
  const [backendLoading, setBackendLoading] = useState(false);
  const [frontendLoading, setFrontendLoading] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [frontendError, setFrontendError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setBackendLoading(true);
    setBackendError(null);
    getDeploymentVersion()
      .then(setBackendVersion)
      .catch((err) => setBackendError(err?.message || 'Failed to load backend version'))
      .finally(() => setBackendLoading(false));

    setFrontendLoading(true);
    setFrontendError(null);
    fetch(getFrontendVersionUrl())
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Not found'))))
      .then(setFrontendVersion)
      .catch(() => {
        setFrontendError(null);
        setFrontendVersion({
          commitHash: null,
          commitShort: null,
          author: null,
          authorEmail: null,
          date: null,
          subject: null,
          body: null,
          tag: null,
          generatedAt: null,
          message: 'Version not available. Run: npm run version:write:frontend before frontend build.',
        });
      })
      .finally(() => setFrontendLoading(false));
  }, [open]);

  return (
    <Modal
      title="Deployment version"
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
    >
      <div className="space-y-4 mt-2">
        <VersionBlock
          title="Backend"
          version={backendVersion}
          loading={backendLoading}
          error={backendError}
          fallbackLabel="Backend version not available. Run: npm run version:write:backend before deploy."
        />
        <VersionBlock
          title="Frontend"
          version={frontendVersion}
          loading={frontendLoading}
          error={frontendError || null}
          fallbackLabel="Frontend version not available. Run: npm run version:write:frontend before build."
        />
      </div>
    </Modal>
  );
};

export default VersionModal;
